import {
  Box,
  Divider,
  Flex,
  Table as TableC,
  TableCaption,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import type { DateTime } from 'luxon'

import { millisecondsToHumanReadableString } from '@/utils/time'

import VolunteerActivityQ from '@/queries/VolunteerActivityQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL, type SQLFragment } from '@/client/query'

import { pageSize } from '@/components/Pagination/constants'
import Pagination, { useOffset } from '@/components/Pagination/Pagination'
import useDateFilter from '@/components/Pagination/useDateFilter'
import useSortWidgetV2 from '@/components/Pagination/useSortWidgetV2'
import { SearchBar, useSearch } from '@/components/Search/Search'
import Tippy from '@/components/UI/Tippy'

import { DownloadPDFModal } from './DownloadPDFModal'
import { getActivityTypeFilters } from './helpers'
import { TableRow } from './TableRow'
import transformVolunteerActivityQuery from './transformVolunteerActivityQuery'

const MIN_DURATION_QUERY_KEY = 'minDuration'

const STATUS_FILTERS = {
  All: SQL`TRUE`,
  'Requires Changes': VolunteerActivityQ.requiresChangesBeforeApproval(),
  Pending: SQL`${
    VolunteerActivityQ.approved_at
  } IS NULL AND NOT ${VolunteerActivityQ.requiresChangesBeforeApproval()}`,
  Approved: SQL`${VolunteerActivityQ.approved_at} IS NOT NULL`,
} as const

export function Table({
  userIdOrQuery,
  userFullName,
  userCreatedAt,
  isDownloadPDFModalOpen = false,
  closeDownloadPDFModal,
  asAdmin = false,
}: {
  userIdOrQuery: number | SQLFragment<number>
  userFullName: string
  userCreatedAt: DateTime
  isDownloadPDFModalOpen?: boolean | null
  closeDownloadPDFModal?: () => void
  asAdmin?: boolean
}): JSX.Element {
  const currentUser = useUser()
  const query = useQuery()
  const offset = useOffset()

  const statusFilters: Record<keyof typeof STATUS_FILTERS, SQLFragment<unknown>> = asAdmin
    ? {
        Pending: STATUS_FILTERS.Pending,
        'Requires Changes': STATUS_FILTERS['Requires Changes'],
        Approved: STATUS_FILTERS.Approved,
        All: STATUS_FILTERS.All,
      }
    : {
        All: STATUS_FILTERS.All,
        'Requires Changes': STATUS_FILTERS['Requires Changes'],
        Pending: STATUS_FILTERS.Pending,
        Approved: STATUS_FILTERS.Approved,
      }

  const { value: statusFilter, Component: StatusFilterWidget } = useSortWidgetV2({
    sorts: statusFilters,
    queryKey: 'statusFilter',
  })

  const typeFilters = getActivityTypeFilters()
  const { value: typeFilter, Component: TypeFilterWidget } = useSortWidgetV2({
    sorts: typeFilters,
    queryKey: 'typeFilter',
  })

  const { startDate, endDate, DateFilterWidget } = useDateFilter({ hideHelpText: true })
  const dateFilter = SQL`
    ${
      startDate != null
        ? SQL`${VolunteerActivityQ.date} >= ${startDate.toUTC().toISO()} AND`
        : SQL``
    }
    ${endDate != null ? SQL`${VolunteerActivityQ.date} <= ${endDate.toUTC().toISO()}` : SQL`TRUE`}
  `

  const minDurationMinutesParam = useSearch(MIN_DURATION_QUERY_KEY)
  const maybeMinDurationMinutes = Number.parseInt(minDurationMinutesParam, 10)
  const minDurationMinutes = Number.isNaN(maybeMinDurationMinutes) ? 0 : maybeMinDurationMinutes

  const sorts = {
    'Activity Date (Newest first)': SQL`${VolunteerActivityQ.date} DESC, ${VolunteerActivityQ.created_at} DESC`,
    'Activity Date (Oldest first)': SQL`${VolunteerActivityQ.date} ASC, ${VolunteerActivityQ.created_at} DESC`,
    'Submitted At (Newest first)': SQL`${VolunteerActivityQ.created_at} DESC`,
    'Submitted At (Oldest first)': SQL`${VolunteerActivityQ.created_at} ASC`,
  }
  const { value: sort, Component: SortDropdown } = useSortWidgetV2({ sorts })

  const where = SQL`${VolunteerActivityQ.where(
    {
      userId: userIdOrQuery,
    },
    SQL`
      ${statusFilter} AND
      ${typeFilter} AND
      ${dateFilter} AND
      CEIL(${VolunteerActivityQ.duration_seconds} / 60::NUMERIC) >= ${minDurationMinutes}
    `
  )}`

  const totalCount = query<number>`COUNT(*) ${where}` ?? 0
  const totalSeconds = query<number>`SUM(${VolunteerActivityQ.duration_seconds}) ${where}` ?? 0
  const totalTimeText = millisecondsToHumanReadableString(totalSeconds * 1000, { maxUnit: 'hour' })

  const paginatedActivities = query
    .many(SQL`${where} ORDER BY ${sort} LIMIT ${pageSize} OFFSET ${offset}`)
    .map(activity => transformVolunteerActivityQuery(currentUser, activity))

  // TODO(justin): This is a temporary hack to get PDF download to work properly.
  const allActivities = query
    .many(SQL`${where} ORDER BY ${sort}`)
    .map(activity => transformVolunteerActivityQuery(currentUser, activity))

  return (
    <>
      {closeDownloadPDFModal != null ? (
        <DownloadPDFModal
          isOpen={isDownloadPDFModalOpen === true}
          onClose={closeDownloadPDFModal}
          userFullName={userFullName}
          startDate={startDate}
          endDate={endDate}
          memberSince={userCreatedAt}
          totalTime={totalTimeText}
          activities={allActivities}
        />
      ) : null}
      <Flex direction="column" flex={1}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'flex-end' }}
          gap="8px"
          mb="16px"
        >
          <DateFilterWidget />
          <Divider
            orientation="vertical"
            display={{ base: 'none', md: 'block' }}
            alignSelf="flex-end"
            height="38px"
          />
          <StatusFilterWidget
            text="Status"
            direction="column"
            fontWeight="medium"
            width={{ base: '100%', md: '150px' }}
          />
          <TypeFilterWidget
            text="Type"
            direction="column"
            fontWeight="medium"
            width={{ base: '100%', md: '150px' }}
          />
          <Flex direction="column" gap="2px" width={{ base: '100%', md: '140px' }}>
            <Box fontWeight="medium">Min Duration (min)</Box>
            <SearchBar queryKey={MIN_DURATION_QUERY_KEY} placeholder="e.g. 30" />
          </Flex>
          <SortDropdown
            text="Sort"
            direction="column"
            fontWeight="medium"
            width={{ base: '100%', md: '220px' }}
          />
        </Flex>

        <TableC borderRadius="8px" size="sm">
          <TableCaption placement="top" bgColor="info.subtle" borderRadius="8px" mb="16px">
            <Flex justify="center" align="center">
              <Tippy content="Based on applied filters.">
                <Text fontWeight={400} fontFamily="body">
                  Total time:{' '}
                  <Text as="span" fontWeight={600}>
                    {totalTimeText}
                  </Text>
                  <Text as="span" fontWeight="bold" color="fg.error">
                    *
                  </Text>
                </Text>
              </Tippy>
            </Flex>
          </TableCaption>

          <Thead>
            <Tr>
              <Th />
              <Th>Activity</Th>
              <Th>Description</Th>
              <Th>Date</Th>
              <Th>Duration</Th>
              <Th>Links</Th>
              <Th>Submitted</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedActivities.map(activity => (
              <TableRow key={activity.id} activity={activity} asAdmin={asAdmin} />
            ))}
          </Tbody>
        </TableC>
        <Pagination scheme="url" totalItemsCount={totalCount} itemsPerPage={pageSize} />
      </Flex>
    </>
  )
}
